import * as React from "react";

import { getPrincipal } from "@p2l/config";
import { useTranslations } from "@p2l/react-utils";
import type { LayoutModel } from "@p2l/ui-public";
import { getCookie } from "@p2l/utils";

import { getTranslatedData } from "./utils";
import { FeedbackDialog } from "./components";
import {
  sendFeedback as sendFeedbackApi,
  sendSuggestion as sendSuggestionApi,
} from "./services/feedback-service";
import translations from "./translations.json";
import { Footer } from "./app";
import { globalFooterInit } from "./global-footer-init";

globalFooterInit();

const whenDocumentParsed = () =>
  new Promise<void>((resolve) => {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => resolve(), {
        once: true,
      });
    } else {
      resolve();
    }
  });

export interface IFooterAppProps {
  /**
   * Set to true if the page should be reloaded on language change. Used for pages in legacy.
   */
  shouldReloadOnLanguageChange?: boolean;

  layout: LayoutModel;
}

const FooterApp = (props: IFooterAppProps) => {
  const { translate, culture } = useTranslations(translations);
  const [shouldOpenFeedbackDialog, setShouldOpenFeedbackDialog] =
    React.useState(false);
  const [successfulFeedbackSave, setSuccessfulFeedbackSave] =
    React.useState(false);
  const [showDataPolicy, setShowDataPolicy] = React.useState(false);

  let principal = getPrincipal();

  React.useEffect(() => {
    window.p2lFooterConfig.CfpConfig.openFeedbackDialog = () =>
      setShouldOpenFeedbackDialog(true);
  }, []);

  React.useEffect(() => {
    let mounted = true;

    // The chrome shell can mount the footer before the host page has run its data-policy-public bootstrap.
    whenDocumentParsed()
      .then(() => window.p2lDataPolicy?.getVisibility())
      .then((visibility) => {
        if (mounted) {
          setShowDataPolicy(visibility?.showInFooter ?? false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const handleFeedbackSend = async (
    formText: string,
    canBeContacted: boolean,
    name?: string,
    email?: string
  ) => {
    const requests = [];

    if (props.layout.showFeedback) {
      requests.push(
        sendFeedbackApi({
          canBeContacted: canBeContacted,
          catalogId: getCookie("catalog"),
          feedbackEmailTo: props.layout.feedbackEmail,
          feedbackText: formText,
          feedbackName: name ?? principal.name,
          feedbackEmailFrom: email ?? principal.email,
        })
      );
    }

    if (props.layout.agent) {
      requests.push(
        sendSuggestionApi({
          content: formText,
          name: name ?? principal.name,
          email: email ?? principal.email,
          canBeContacted: canBeContacted,
        })
      );
    }

    if (requests.length === 0) {
      return;
    }

    try {
      const results = await Promise.allSettled(requests);

      const hasSuccess = results.some(
        (res: any) => res.status === "fulfilled" && res.value.status === 200
      );
      const hasFailure = results.some(
        (res: any) =>
          res.status === "rejected" ||
          (res.status === "fulfilled" && res.value.status !== 200)
      );

      if (hasSuccess) {
        setSuccessfulFeedbackSave(true);
      }

      if (hasFailure) {
        window.p2lToast.add({ title: translate("ERROR_OCCURED") });
      }
    } catch {
      window.p2lToast.add({ title: translate("ERROR_OCCURED") });
    }
  };

  return (
    <>
      <Footer
        customFooterHTML={props.layout.footerContent}
        shouldReloadOnLanguageChange={props.shouldReloadOnLanguageChange}
        showPdfCatalog={props.layout.pdfCatalog}
        feedback={
          props.layout.showFeedback
            ? {
                name:
                  getTranslatedData(culture, props.layout.feedbackName) || "",
                onClick: () =>
                  setShouldOpenFeedbackDialog(!shouldOpenFeedbackDialog),
                feedbackEmail: props.layout.feedbackEmail,
              }
            : undefined
        }
        dataPolicy={
          showDataPolicy
            ? {
                name: translate("DATA_POLICY"),
                onClick: () => window.p2lDataPolicy?.open(),
              }
            : undefined
        }
      />
      <FeedbackDialog
        open={shouldOpenFeedbackDialog}
        onClose={() => {
          setShouldOpenFeedbackDialog(false);
          setSuccessfulFeedbackSave(false);
        }}
        title={translate("FEEDBACK_DEFAULT_TITLE")}
        successfulFeedbackSave={successfulFeedbackSave}
        onSend={handleFeedbackSend}
      />
    </>
  );
};

export default FooterApp;
